import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchActiveOrders } from "../../redux/logistics/orders/ordersSlice";
import { openModal } from "redux/navigation/modalStates"
import Settings from "components/Options/Settings";
import TableStyle from "components/Options/TableStyle";
import { OrderNotes } from "components/Warehouse/rows/order";
import { stringToColor } from "components/Orders/helpers";
import toast from "react-hot-toast";
import { pushToStarboard } from "views/Orders";
import SearchableMultiSelect from "components/UI/Atoms/inputs/selects/searchableMultiselect";

/** helpers to persist settings */
function loadSettings(lane) {
    try {
        const saved = localStorage.getItem(`allOrdersTableSettings-${lane}`);
        return saved ? JSON.parse(saved) : {};
    } catch {
        return {};
    }
}

function saveSettings(settings, lane) {
    localStorage.setItem(`allOrdersTableSettings-${lane}`, JSON.stringify(settings));
}

function clearSettings(lane) {
    localStorage.removeItem(`allOrdersTableSettings-${lane}`);
}

/** debounce hook */
function useDebounce(value, delay = 300) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
}

/** date helpers */
function pad2(n) { return n.toString().padStart(2, "0"); }
function nextDayISO(yyyyMmDd) {
    if (!yyyyMmDd) return "";
    const d = new Date(yyyyMmDd + "T00:00:00");
    d.setDate(d.getDate() + 1);
    const y = d.getFullYear();
    const m = pad2(d.getMonth() + 1);
    const day = pad2(d.getDate());
    return `${y}-${m}-${day}`;
}

/** tiny IconToggle group */
function IconToggle({ value, onChange, options }) {
    return (
        <div className="aot-icon-toggle">
            {options.map((opt) => (
                <button
                    key={opt.value}
                    type="button"
                    className={`button square mini aot-icon-toggle__btn ${value === opt.value ? "active" : "non-active"}`}
                    onClick={() => onChange(opt.value)}
                    title={opt.title}
                    aria-pressed={value === opt.value}
                >
                    <i className={`fa ${opt.icon}`} />
                </button>
            ))}
        </div>
    );
}

export const AllOrdersTable = ({ lane }) => {
    const dispatch = useDispatch();
    const { loading, error, orders } = useSelector((state) => state.orders.activeOrders);
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalPages, setTotalPages] = useState(1);
    const [hoveredColumn, setHoveredColumn] = useState(null);

    const saved = loadSettings(lane);
    const [ordering, setOrdering] = useState(saved.ordering || "delivery_date"); // e.g. "pickup_date" or "-pickup_date"

    // text filters (values)
    const [filters, setFilters] = useState(saved.filters || {});

    // column visibility
    const [columnVisibility, setColumnVisibility] = useState(saved.columnVisibility || {});

    // range states
    const [dateRanges, setDateRanges] = useState(saved.dateRanges || {});       // { [key]: { from, to } }
    const [numberRanges, setNumberRanges] = useState(saved.numberRanges || {}); // { [key]: { min, max } }

    // exact states
    const [dateExact, setDateExact] = useState(saved.dateExact || {});          // { [key]: 'YYYY-MM-DD' }
    const [numberExact, setNumberExact] = useState(saved.numberExact || {});    // { [key]: '123' }

    // modes per column
    // - for number/date: { [key]: 'range' | 'exact' (numbers), 'range' | 'on' (dates) }
    // - for text: { [key]: { textLookup: 'icontains' | 'iexact' | 'istartswith' | 'iendswith' } }
    const [filterModes, setFilterModes] = useState(saved.filterModes || {});

    // Drag and drop state
    const [columnOrder, setColumnOrder] = useState(saved.columnOrder || []);
    const [draggedColumn, setDraggedColumn] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    const [activeColumn, setActiveColumn] = useState(null);
    const [columnFilterValues, setColumnFilterValues] = useState(saved.filters || {}); // UI text inputs

    // popup animation state
    const [popupStage, setPopupStage] = useState("idle"); // "idle" | "enter" | "entered"
    const [closingColumn, setClosingColumn] = useState(null);
    const popupRef = useRef(null);
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

    const debouncedSearch = useDebounce(searchTerm, 300);

    const [laneReady, setLaneReady] = useState(false);

    /** columns definition with filter spec */
    const baseColumns = [
        {
            key: "order_no", label: "Pro",
            filter: { type: "text", lookup: "icontains" },
            accessor: (o) => (
                <div className="aot-col-stack">
                    <span className="aot-nowrap">{o.order_no} {o.order_group}</span>
                    <span className="aot-nowrap">{o.pro_f}</span>
                </div>
            ),
            filterable: true
        },
        {
            key: "team", label: "Team",
            filter: { type: "text", lookup: "icontains" },
            accessor: (o) => (
                <div className="aot-col-stack">
                    {o.reference_3}
                </div>
            ), filterable: true
        },
        {
            key: "dispatch_board", label: "Service Level",
            filter: { type: "text", lookup: "icontains" },
            accessor: (o) => o.dispatch_board, filterable: true
        },
        {
            key: "units", label: "Units",
            filter: { type: "number-range", lookups: { min: "gte", max: "lte" } },
            accessor: (o) => o.units, filterable: true
        },
        {
            key: "total_length", label: "T. Length",
            filter: { type: "number-range", lookups: { min: "gte", max: "lte" } },
            accessor: (o) => o.total_length, filterable: true
        },
        {
            key: "weight", label: "Weight",
            filter: { type: "number-range", lookups: { min: "gte", max: "lte" } },
            accessor: (o) => o.weight, filterable: true
        },
        {
            key: "vehicle_size", label: "Vehicle",
            filter: { type: "text", lookup: "icontains" },
            accessor: (o) => o.vehicle_size, filterable: true
        },
        {
            key: "pickup_date", label: "Pick. Date",
            filter: { type: "date-range", lookups: { from: "gte", to: "lte" } },
            accessor: (o) => (<div className="aot-col-stack">{o.pickup_date}</div>), filterable: true
        },
        {
            key: "pickup_at", label: "Pick. City",
            filter: { type: "text", lookup: "icontains" },
            accessor: (o) => o.pickup_at, filterable: true
        },
        {
            key: "delivery_date",
            label: "Del. Date",
            filter: { type: "date-range", lookups: { from: "gte", to: "lte" } },
            accessor: (o) => (<div className="aot-col-stack">{o.delivery_date}</div>), filterable: true
        },
        {
            key: "deliver_to",
            label: "Del. City",
            filter: { type: "text", lookup: "icontains" },
            accessor: (o) => o.deliver_to, filterable: true
        },
        {
            key: "mx_documents_ref",
            backendKey: "backorder__mx_documents",
            label: "Documents",
            filter: { type: "text", lookup: "icontains" },
            accessor: (o) => (
                <span
                    className={
                        (o.mx_documents_ref.length > 2 && o.mx_documents_ref !== "0" ? "cyellow" : "") +
                        " " +
                        (o.mx_documents_ref.includes("DODA") && o.mx_documents_ref.includes("Prefile") ? "cgreen" : "cred")
                    }
                >
                    {o.mx_documents_ref}
                </span>
            ),
            filterable: false,
            sortable: false
        },
        {
            key: "dock",
            backendKey: "dock",
            label: "Dock",
            filter: { type: "text", lookup: "icontains" },
            accessor: (o) => (<span className={TableStyle.GetStatusClasses(o)}>{o.dock}</span>), filterable: true
        },
        {
            key: "outbound",
            backendKey: "bus",
            label: "Outbound",
            filter: { type: "text", lookup: "icontains" },
            accessor: (o) => (
                <>
                    <span
                        className="aot-bus-pill"
                        style={{ backgroundColor: o.terminal === 4 ? stringToColor(o.bus) : "" }}
                    >
                        {o.bus}
                    </span>
                    {o.trailer_number && (
                        <span className="text-danger text-uppercase font-weight-bold">{` | ${o.trailer_number}`}</span>
                    )}
                </>
            ), filterable: true
        },
        {
            key: "stop_notes",
            backendKey: "notes",
            label: "Stop / Notes",
            filter: { type: "text", lookup: "icontains" },
            accessor: (o) => (
                <div className="d-flex flex-column align-items-center">
                    <span className={o.stop === "AA" || o.stop === "DROP" ? "badge badge-warning" : ""}>{o.stop}</span>
                    <div><OrderNotes order={o} /></div>
                </div>
            ),
            filterable: true
        },
        {
            key: "distance", label: "Distance",
            filter: { type: "number-range", lookups: { min: "gte", max: "lte" } },
            accessor: (o) => o.distance, filterable: true
        },
        {
            key: "drop_dead", label: "Drop Dead",
            filter: { type: "date-range", lookups: { from: "gte", to: "lte" } },
            accessor: (o) => (<span className={Settings.checkDropDead(o.drop_dead)}>{o.drop_dead}</span>), filterable: true
        },
        {
            key: "act",
            label: "Act.",
            accessor: (row) => (
                <div className="aot-actions">
                    <button
                        className="btn btn-primary btn-xs"
                        title="Show Order Details"
                        onClick={() => {
                            dispatch(openModal({
                                type: "ORDER_DETAILS",
                                props: { orderId: row.id }
                            }))
                        }}
                    >
                        <i className="fa fa-eye" />
                    </button>
                    <button
                        className="btn btn-primary btn-xs"
                        onClick={() => pushToStarboard(row.id)}
                        title="Push To Starboard"
                    >
                        <i className="fa fa-upload" />
                    </button>
                </div>
            ),
            filterable: false,
            sortable: false,
        },
    ];

    // Get columns in the correct order
    const columns = (columnOrder && columnOrder.length > 0
        ? columnOrder.map(key => baseColumns.find(col => col.key === key)).filter(Boolean)
        : baseColumns);

    // put this helper above the component body if you like
    const defaultColumnOrder = (cols) => cols.map(c => c.key);

    const mergeColumnOrder = (savedOrder, cols) => {
        const baseKeys = cols.map((c) => c.key);
        const validSaved = (savedOrder || []).filter((k) => baseKeys.includes(k));
        const missing = baseKeys.filter((k) => !validSaved.includes(k));
        return [...validSaved, ...missing];
    };

    useEffect(() => {
        setLaneReady(false);            // <- pause fetching while we swap settings

        const s = loadSettings(lane) || {};

        // apply per-lane settings (use lane defaults, not previous lane's state)
        setOrdering(s.ordering ?? "delivery_date");
        setFilters(s.filters ?? {});
        setColumnVisibility(s.columnVisibility ?? {});
        setDateRanges(s.dateRanges ?? {});
        setNumberRanges(s.numberRanges ?? {});
        setDateExact(s.dateExact ?? {});
        setNumberExact(s.numberExact ?? {});
        setFilterModes(s.filterModes ?? {});
        setColumnFilterValues(s.filters ?? {});
        setSearchTerm("");
        setPage(1);

        setColumnOrder(
            Array.isArray(s.columnOrder) && s.columnOrder.length
                ? mergeColumnOrder(s.columnOrder, baseColumns)
                : defaultColumnOrder(baseColumns)
        );

        setLaneReady(true);
    }, [lane]);

    // persist settings whenever they change
    useEffect(() => {
        saveSettings(
            {
                ordering,
                filters,
                columnVisibility,
                dateRanges,
                numberRanges,
                dateExact,
                numberExact,
                filterModes,
                columnOrder
            },
            lane
        );
    }, [ordering, filters, columnVisibility, dateRanges, numberRanges, dateExact, numberExact, filterModes, columnOrder, lane]);

    // Helper function to get the correct backend key for a column
    const getBackendKey = (col) => {
        return col.backendKey || col.key;
    };

    // Build params from filter spec + modes
    const buildFilterParams = () => {
        const params = {
            page,
            page_size: pageSize,
            search: debouncedSearch,
        };

        if (lane !== null) {
            params.terminal = lane;
        }
        if (ordering) params.ordering = ordering;

        columns.forEach((col) => {
            if (!col.filterable || !col.filter) return;
            const spec = col.filter;
            const backendKey = getBackendKey(col);

            if (spec.type === "text") {
                const val = filters[col.key];
                if (val) {
                    const mode = filterModes[col.key]?.textLookup || spec.lookup || "icontains";
                    params[`${backendKey}__${mode}`] = val;
                }
            }

            if (spec.type === "number-range") {
                const mode = filterModes[col.key]?.mode || "range"; // 'range' | 'exact'
                if (mode === "exact") {
                    const v = numberExact[col.key];
                    if (v !== undefined && v !== "") params[backendKey] = v;
                } else {
                    const r = numberRanges[col.key] || {};
                    const minLookup = spec.lookups?.min || "gte";
                    const maxLookup = spec.lookups?.max || "lte";
                    if (r.min !== undefined && r.min !== "") params[`${backendKey}__${minLookup}`] = r.min;
                    if (r.max !== undefined && r.max !== "") params[`${backendKey}__${maxLookup}`] = r.max;
                }
            }

            if (spec.type === "date-range") {
                const mode = filterModes[col.key]?.mode || "range"; // 'range' | 'on'
                if (mode === "on") {
                    const d = dateExact[col.key];
                    if (d) {
                        params[`${backendKey}__gte`] = d;
                        params[`${backendKey}__lt`] = nextDayISO(d);
                    }
                } else {
                    const r = dateRanges[col.key] || {};
                    const fromLookup = spec.lookups?.from || "gte";
                    const toLookup = spec.lookups?.to || "lte";
                    if (r.from) params[`${backendKey}__${fromLookup}`] = r.from;
                    if (r.to) params[`${backendKey}__${toLookup}`] = r.to;
                }
            }
        });

        return params;
    };

    // fetch data whenever inputs change
    useEffect(() => {
        if (!laneReady) return; // <— block the early call
        const params = buildFilterParams();
        dispatch(fetchActiveOrders(params));
    }, [
        laneReady,     // <— key
        dispatch,
        page,
        pageSize,
        lane,
        debouncedSearch,
        ordering,
        filters,
        dateRanges,
        numberRanges,
        dateExact,
        numberExact,
        filterModes,
    ]);

    // total pages from server count
    useEffect(() => {
        const count = orders?.count ?? 0;
        const pages = Math.max(1, Math.ceil(count / pageSize));
        setTotalPages(pages);
        if (page > pages) setPage(pages);
    }, [orders?.count, page, pageSize]);

    // reset pagination when filter specs change
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, lane, pageSize, ordering, filters, dateRanges, numberRanges, dateExact, numberExact, filterModes]);

    // open animation trigger for popup
    useEffect(() => {
        if (activeColumn) {
            setPopupStage("enter");
            const id = requestAnimationFrame(() => setPopupStage("entered"));
            return () => cancelAnimationFrame(id);
        } else {
            setPopupStage("idle");
        }
    }, [activeColumn]);

    // outside click -> close with animation
    useEffect(() => {
        const handleClick = (e) => {
            if (activeColumn && popupRef.current && !popupRef.current.contains(e.target)) {
                closeWithAnim(activeColumn);
            }
        };
        document.addEventListener("click", handleClick);
        return () => document.removeEventListener("click", handleClick);
    }, [activeColumn]);

    // toast on error
    useEffect(() => {
        if (!error) return;
        console.error("Error loading orders:", error);
        const msg =
            typeof error === "string"
                ? error
                : error?.message || error?.detail || error?.msg || JSON.stringify(error);
        toast.error("Error loading orders: " + msg);
    }, [error]);

    // ===== Drag & Drop: live reordering on dragOver =====
    const handleDragStart = (e, columnKey) => {
        e.dataTransfer.setData("text/plain", columnKey);
        setDraggedColumn(columnKey);
        setIsDragging(true);
        // Make the dragged header slightly transparent
        e.currentTarget.style.opacity = "0.5";
    };

    const handleDragOver = (e, targetKey) => {
        e.preventDefault();
        if (!draggedColumn || draggedColumn === targetKey) return;

        setColumnOrder((prev) => {
            const newOrder = [...prev];
            const fromIndex = newOrder.indexOf(draggedColumn);
            const toIndex = newOrder.indexOf(targetKey);

            if (fromIndex === -1 || toIndex === -1) return prev;
            if (fromIndex === toIndex) return prev;

            newOrder.splice(fromIndex, 1);
            newOrder.splice(toIndex, 0, draggedColumn);

            return newOrder;
        });
    };

    const handleDragEnd = (e) => {
        e.preventDefault();
        setDraggedColumn(null);
        setIsDragging(false);
        if (e.currentTarget) e.currentTarget.style.opacity = "1";
    };
    // ================================================

    const showColumnPopup = (colKey, event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const popupWidth = 500;
        const popupHeight = 360;

        let left = rect.left;
        let top = rect.bottom;

        if (left + popupWidth > viewportWidth) {
            left = viewportWidth - popupWidth - 10;
        }
        if (top + popupHeight > viewportHeight) {
            top = rect.top - popupHeight;
        }

        setPopupPosition({ top: top + window.scrollY, left: left + window.scrollX });
        setActiveColumn(activeColumn === colKey ? null : colKey);
        event.stopPropagation();
    };

    const closeWithAnim = (key) => {
        setClosingColumn(key);
        setTimeout(() => {
            setActiveColumn(null);
            setClosingColumn(null);
        }, 160);
    };

    // setters
    const setFilterAuto = (colKey, value) => {
        setColumnFilterValues((prev) => ({ ...prev, [colKey]: value }));
        setFilters((prev) => {
            const next = { ...prev };
            if (value && value.trim() !== "") next[colKey] = value;
            else delete next[colKey];
            return next;
        });
    };

    const setDateRange = (colKey, which, value) => {
        setDateRanges((prev) => {
            const current = prev[colKey] || {};
            const updated = { ...current, [which]: value };
            if (!updated.from && !updated.to) {
                const clone = { ...prev }; delete clone[colKey]; return clone;
            }
            return { ...prev, [colKey]: updated };
        });
    };
    const setDateOn = (colKey, value) => {
        setDateExact((prev) => {
            const next = { ...prev };
            if (!value) delete next[colKey];
            else next[colKey] = value;
            return next;
        });
    };

    const setNumberRange = (colKey, which, value) => {
        setNumberRanges((prev) => {
            const current = prev[colKey] || {};
            const updated = { ...current, [which]: value };
            if (!updated.min && !updated.max) {
                const clone = { ...prev }; delete clone[colKey]; return clone;
            }
            return { ...prev, [colKey]: updated };
        });
    };
    const setNumberExactValue = (colKey, value) => {
        setNumberExact((prev) => {
            const next = { ...prev };
            if (value === "" || value === undefined) delete next[colKey];
            else next[colKey] = value;
            return next;
        });
    };

    const setMode = (colKey, modeOrLookup, type) => {
        // type: 'text-lookup' | 'number-mode' | 'date-mode'
        setFilterModes((prev) => {
            const current = prev[colKey] || {};
            let updated;
            if (type === "text-lookup") {
                updated = { ...current, textLookup: modeOrLookup };
            } else {
                updated = { ...current, mode: modeOrLookup };
            }
            return { ...prev, [colKey]: updated };
        });
    };

    const clearEverythingFor = (colKey) => {
        setFilters((p) => { const n = { ...p }; delete n[colKey]; return n; });
        setDateRanges((p) => { const n = { ...p }; delete n[colKey]; return n; });
        setNumberRanges((p) => { const n = { ...p }; delete n[colKey]; return n; });
        setDateExact((p) => { const n = { ...p }; delete n[colKey]; return n; });
        setNumberExact((p) => { const n = { ...p }; delete n[colKey]; return n; });
        setFilterModes((p) => { const n = { ...p }; delete n[colKey]; return n; });
        setColumnFilterValues((p) => { const n = { ...p }; delete n[colKey]; return n; });
    };

    // Clear EVERYTHING: search, sort, text filters, ranges, exacts, modes, inputs
    const clearAll = () => {
        setSearchTerm("");
        setOrdering("");
        setFilters({});
        setDateRanges({});
        setNumberRanges({});
        setDateExact({});
        setNumberExact({});
        setFilterModes({});
        setColumnFilterValues({});
        setActiveColumn(null);
        setPage(1);
        clearSettings(lane);
    };

    // NEW: only counts filters for the ACTIVE mode
    const hasActiveFilter = (col) => {
        if (!col?.filterable || !col.filter) return false;
        const k = col.key;
        const spec = col.filter;

        // TEXT: mode only changes lookup, not which input is used
        if (spec.type === "text") {
            return Boolean(filters[k] && String(filters[k]).trim() !== "");
        }

        // NUMBER
        if (spec.type === "number-range") {
            const mode = filterModes[k]?.mode || "range"; // 'range' | 'exact'
            if (mode === "exact") {
                const v = numberExact[k];
                return v !== undefined && v !== "";
            }
            const r = numberRanges[k] || {};
            return (r.min !== undefined && r.min !== "") || (r.max !== undefined && r.max !== "");
        }

        // DATE
        if (spec.type === "date-range") {
            const mode = filterModes[k]?.mode || "range"; // 'range' | 'on'
            if (mode === "on") return Boolean(dateExact[k]);
            const r = dateRanges[k] || {};
            return Boolean(r.from || r.to);
        }

        return false;
    };

    // Helper function to get the correct ordering key for a column
    const getOrderingKey = (col) => {
        return col.backendKey || col.key;
    };

    const columnOptions = columns.map((col) => ({
        value: col.key,
        label: col.label,
    }));

    const visibleColumnKeys = columns
        .filter((col) => columnVisibility[col.key] !== false)
        .map((col) => col.key);

    const handleColumnVisibilityChange = (nextValues) => {
        const selectedKeys = Array.isArray(nextValues) ? nextValues : [];
        setColumnVisibility((prev) => {
            const next = { ...prev };
            columns.forEach((col) => {
                if (selectedKeys.includes(col.key)) {
                    delete next[col.key];
                } else {
                    next[col.key] = false;
                }
            });
            return next;
        });
    };

    return (
        <div className="aot-root">
            {error && (
                <div className="alert alert-danger" role="alert" style={{ marginBottom: 12 }}>
                    {String(error?.message || error?.detail || error?.msg || "Unknown error")}
                </div>
            )}

            {/* Search, column picker, and filter controls */}
            <div className="aot-search-bar">
                <div className="search-container">
                    <div className="aot-field aot-field--search">
                        <div className="aot-field-control">
                            <label className="aot-field-label" htmlFor="orders-search">
                                <i className="fa fa-search aot-field-label-icon" />
                                <span>Search</span>
                            </label>
                            <div className="aot-search-input-container">
                                <input
                                    id="orders-search"
                                    className="aot-search-input"
                                    placeholder="Search orders… separate terms with commas to search multiple fields" 
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="aot-column-picker aot-field">
                        <div className="aot-field-control">
                            <label className="aot-field-label aot-field-label--columns">
                                <i className="fa fa-columns aot-field-label-icon" />
                                <span>Columns</span>
                            </label>
                            <SearchableMultiSelect
                                options={columnOptions}
                                value={visibleColumnKeys}
                                onChange={handleColumnVisibilityChange}
                                placeholder="Columns…"
                                selectAllLabel="Show all (filtered)"
                                clearLabel="Hide (filtered)"
                                noResultsText="No columns"
                                maxMenuHeight={260}
                                chipLimit={2}
                            />
                        </div>
                    </div>
                </div>
                <div>
                    <button
                        className="aot-clear-btn aot-clear-btn--label"
                        onClick={clearAll}
                        title="Clear table filters"
                        aria-label="Clear table filters"
                    >
                        <i className="fa fa-filter aot-clear-icon" />
                        <span className="aot-clear-text">Clear filters</span>
                    </button>
                </div>
            </div>

            <div className="shaddow-container">
                <div className="order-table-inner-container">
                    <div className="aot-drag-hint" aria-hidden>
                        <i className="fa fa-arrows-alt-h" aria-hidden />
                        Drag headers to reorder columns
                    </div>
                    <div className="table-scroll">
                        <table className="briefing-table all-orders-table">
                            <thead>
                                <tr>
                                    {columns.map((col) => (
                                        <th
                                            key={col.key}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, col.key)}
                                            onDragOver={(e) => handleDragOver(e, col.key)}
                                            onDragEnd={handleDragEnd}
                                            onContextMenu={(e) => {
                                                e.preventDefault();
                                                showColumnPopup(col.key, e);
                                            }}
                                            className={`aot-th ${isDragging ? "aot-th--dragging" : ""}`}
                                            style={{
                                                width: columnVisibility[col.key] === false ? "10px" : "auto",
                                                cursor: isDragging ? "grabbing" : "grab",
                                                opacity: draggedColumn === col.key ? 0.5 : 1,
                                            }}
                                            onMouseEnter={() => setHoveredColumn(col.key)}
                                            onMouseLeave={() => setHoveredColumn(null)}
                                            title="Drag to reorder"
                                        >
                                            <div className="aot-th-inner">
                                                <span
                                                    className="aot-th-label"
                                                    onClick={() => {
                                                        const orderingKey = getOrderingKey(col);
                                                        if (ordering === orderingKey) setOrdering(`-${orderingKey}`);
                                                        else if (ordering === `-${orderingKey}`) setOrdering("");
                                                        else setOrdering(orderingKey);
                                                    }}
                                                >
                                                    {columnVisibility[col.key] === false && hoveredColumn !== col.key ? (
                                                        "⋯"
                                                    ) : (
                                                        <>
                                                            {col.label}
                                                            {ordering === getOrderingKey(col) && <i className="fa fa-arrow-up" />}
                                                            {ordering === `-${getOrderingKey(col)}` && <i className="fa fa-arrow-down" />}
                                                        </>
                                                    )}
                                                </span>

                                                {hasActiveFilter(col) && (
                                                    <i
                                                        className="fa fa-filter aot-filter-icon"
                                                        title="Filter applied"
                                                    />
                                                )}
                                                {columnVisibility[col.key] !== false || hoveredColumn === col.key ? (
                                                    <div>
                                                        <i
                                                            className="fa fa-cog aot-cog-icon"
                                                            onClick={(e) => showColumnPopup(col.key, e)}
                                                            title="Column options"
                                                        />
                                                    </div>
                                                ) : null}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {(orders?.results ?? []).map((row) => (
                                    <tr key={row.id}>
                                        {columns.map((col) => (
                                            <td
                                                key={col.key}
                                                className="aot-td"
                                                style={{
                                                    width: columnVisibility[col.key] === false ? "20px" : "auto",
                                                }}
                                            >
                                                <div className="aot-td-inner">
                                                    {columnVisibility[col.key] === false && hoveredColumn !== col.key
                                                        ? "⋯"
                                                        : col.accessor(row)}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Popup */}
            {activeColumn && (
                <div
                    ref={popupRef}
                    role="dialog"
                    aria-label={`${columns.find((c) => c.key === activeColumn)?.label} column menu`}
                    className="aot-popup"
                    style={{
                        position: "fixed",
                        top: `${popupPosition.top}px`,
                        left: `${popupPosition.left}px`,
                        opacity: closingColumn === activeColumn ? 0 : popupStage === "enter" ? 0 : 1,
                        transform:
                            closingColumn === activeColumn
                                ? "translateY(-4px) scale(0.98)"
                                : popupStage === "enter"
                                    ? "translateY(-6px) scale(0.98)"
                                    : "translateY(0) scale(1)",
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* caret */}
                    <div className="aot-popup-caret" />

                    {/* header */}
                    <div className="aot-popup-header">
                        <strong className="aot-popup-title">
                            {columns.find((c) => c.key === activeColumn)?.label}
                        </strong>
                    </div>

                    {/* body */}
                    <div className="aot-popup-body">
                        {/* FILTER UI with icon toggles */}
                        {(() => {
                            const col = columns.find((c) => c.key === activeColumn);
                            if (!col?.filterable || !col.filter) return null;

                            // TEXT
                            if (col.filter.type === "text") {
                                const currentLookup = filterModes[activeColumn]?.textLookup || col.filter.lookup || "icontains";
                                return (
                                    <div>
                                        <div className="aot-filter-row">
                                            <label className="text-muted aot-muted-label">Match</label>
                                            <IconToggle
                                                value={currentLookup}
                                                onChange={(v) => setMode(activeColumn, v, "text-lookup")}
                                                options={[
                                                    { value: "icontains", icon: "fa-search", label: "Contains", short: "Contains", title: "Contains (case-insensitive)" },
                                                    { value: "iexact", icon: "fa-equals", label: "Exact", short: "Exact", title: "Exact match (case-insensitive)" },
                                                ]}
                                            />
                                        </div>

                                        <label className="text-muted aot-muted-label aot-label-block">
                                            Filter {col.label}
                                        </label>
                                        <input
                                            autoFocus
                                            className="input thin aot-input"
                                            placeholder="Type to filter…"
                                            value={columnFilterValues[col.key] || ""}
                                            onChange={(e) => setFilterAuto(col.key, e.target.value)}
                                            onKeyDown={(e) => { if (e.key === "Escape") closeWithAnim(activeColumn); }}
                                        />
                                    </div>
                                );
                            }

                            // NUMBER
                            if (col.filter.type === "number-range") {
                                const mode = filterModes[activeColumn]?.mode || "range"; // 'range' | 'exact'
                                const r = numberRanges[activeColumn] || {};
                                const ex = numberExact[activeColumn] ?? "";

                                return (
                                    <div>
                                        <div className="aot-filter-row">
                                            <label className="text-muted aot-muted-label">Match</label>
                                            <IconToggle
                                                value={mode}
                                                onChange={(v) => setMode(activeColumn, v, "number-mode")}
                                                options={[
                                                    { value: "range", icon: "fa-sort", label: "Range", short: "Range", title: "Use min/max" },
                                                    { value: "exact", icon: "fa-bullseye", label: "Exact", short: "Exact", title: "Exact value" },
                                                ]}
                                            />
                                        </div>

                                        {mode === "exact" ? (
                                            <div>
                                                <label className="text-muted aot-muted-label aot-label-block">
                                                    Exact value
                                                </label>
                                                <input
                                                    type="number"
                                                    step="any"
                                                    className="input thin aot-input"
                                                    placeholder="e.g. 10"
                                                    value={ex}
                                                    onChange={(e) => setNumberExactValue(activeColumn, e.target.value)}
                                                />
                                            </div>
                                        ) : (
                                            <div>
                                                <label className="text-muted aot-muted-label aot-label-block">
                                                    Number range
                                                </label>
                                                <div className="aot-number-grid">
                                                    <input
                                                        type="number"
                                                        step="any"
                                                        className="input thin aot-input"
                                                        placeholder="Min"
                                                        value={r.min ?? ""}
                                                        onChange={(e) => setNumberRange(activeColumn, "min", e.target.value)}
                                                    />
                                                    <input
                                                        type="number"
                                                        step="any"
                                                        className="input thin aot-input"
                                                        placeholder="Max"
                                                        value={r.max ?? ""}
                                                        onChange={(e) => setNumberRange(activeColumn, "max", e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            // DATE
                            if (col.filter.type === "date-range") {
                                const mode = filterModes[activeColumn]?.mode || "range"; // 'range' | 'on'
                                const r = dateRanges[activeColumn] || {};
                                const ex = dateExact[activeColumn] || "";

                                return (
                                    <div>
                                        <div className="aot-filter-row">
                                            <label className="text-muted aot-muted-label">Match</label>
                                            <IconToggle
                                                value={mode}
                                                onChange={(v) => setMode(activeColumn, v, "date-mode")}
                                                options={[
                                                    { value: "range", icon: "fa-calendar", label: "Range", short: "Range", title: "From/To" },
                                                    { value: "on", icon: "fa-bullseye", label: "On", short: "On", title: "Single day" },
                                                ]}
                                            />
                                        </div>

                                        {mode === "on" ? (
                                            <div>
                                                <label className="text-muted aot-muted-label aot-label-block">
                                                    On date
                                                </label>
                                                <input
                                                    type="date"
                                                    className="input thin aot-input"
                                                    value={ex}
                                                    onChange={(e) => setDateOn(activeColumn, e.target.value)}
                                                />
                                            </div>
                                        ) : (
                                            <div>
                                                <label className="text-muted aot-muted-label aot-label-block">
                                                    Date range
                                                </label>
                                                <div className="aot-date-range-grid">
                                                    <input
                                                        type="date"
                                                        className="input thin aot-input"
                                                        value={r.from || ""}
                                                        onChange={(e) => setDateRange(activeColumn, "from", e.target.value)}
                                                    />
                                                    <i className="fa fa-arrow-right text-muted aot-arrow-center" />
                                                    <input
                                                        type="date"
                                                        className="input thin aot-input"
                                                        value={r.to || ""}
                                                        onChange={(e) => setDateRange(activeColumn, "to", e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            return null;
                        })()}
                    </div>

                    {/* footer */}
                    <div className="aot-popup-footer">
                        <button
                            type="button"
                            className="button mini"
                            onClick={() => {
                                setColumnVisibility(prev => {
                                    const next = { ...prev };
                                    const hidden = next[activeColumn] === false; // only false means hidden
                                    if (hidden) {
                                        // show: remove the override
                                        delete next[activeColumn];
                                    } else {
                                        // hide: set explicit false
                                        next[activeColumn] = false;
                                    }
                                    return next;
                                });
                            }}
                            title={columnVisibility[activeColumn] === false ? "Show column" : "Hide column"}
                        >
                            <i
                                className={`fa ${columnVisibility[activeColumn] === false ? "fa-eye-slash" : "fa-eye"}`}
                                style={{ marginRight: 6 }}
                            />
                            {columnVisibility[activeColumn] === false ? "Show" : "Hide"}
                        </button>

                        <button
                            type="button"
                            className="button mini"
                            onClick={() => {
                                if (ordering === getOrderingKey(activeColumn) || ordering === `-${getOrderingKey(activeColumn)}`) setOrdering("");
                                clearEverythingFor(activeColumn);
                                closeWithAnim(activeColumn);
                            }}
                        >
                            Reset column
                        </button>
                    </div>
                </div>
            )}

            <hr className="aot-divider" />

            {/* pagination */}
            <div className="pager">
                <div className="pager__left">
                    <span className="pager__range">
                        {(() => {
                            const count = orders?.count ?? 0;
                            if (!count) return "No results";
                            const start = (page - 1) * pageSize + 1;
                            const end = Math.min(page * pageSize, count);
                            return `Showing ${start}–${end} of ${count}`;
                        })()}
                    </span>

                    <div className="pager__nav" role="navigation" aria-label="Pagination">
                        <button
                            className="pager__btn"
                            onClick={() => setPage(1)}
                            disabled={page <= 1}
                            aria-label="First page"
                            title="First page"
                        >
                            <i className="fa fa-angle-double-left" />
                        </button>
                        <button
                            className="pager__btn"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page <= 1}
                            aria-label="Previous page"
                            title="Previous page"
                        >
                            <i className="fa fa-chevron-left" />
                        </button>

                        <div className="pager__status" aria-live="polite">
                            <input
                                min={1}
                                max={totalPages}
                                value={page}
                                onChange={(e) => {
                                    const v = Number(e.target.value);
                                    if (Number.isNaN(v)) return;
                                    const clamped = Math.max(1, Math.min(totalPages || 1, v));
                                    setPage(clamped);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        const v = Number(e.currentTarget.value);
                                        const clamped = Math.max(1, Math.min(totalPages || 1, v));
                                        setPage(clamped);
                                        e.currentTarget.blur();
                                    }
                                }}
                                className="pager__input"
                                aria-label="Current page"
                            />
                            <span className="pager__total">/ {totalPages}</span>
                        </div>

                        <button
                            className="pager__btn"
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                            aria-label="Next page"
                            title="Next page"
                        >
                            <i className="fa fa-chevron-right" />
                        </button>
                        <button
                            className="pager__btn"
                            onClick={() => setPage(totalPages)}
                            disabled={page >= totalPages}
                            aria-label="Last page"
                            title="Last page"
                        >
                            <i className="fa fa-angle-double-right" />
                        </button>
                    </div>
                </div>

                <div className="pager__right">
                    <label className="pager__label" htmlFor="page-size">Rows:</label>
                    <select
                        id="page-size"
                        className="pager__select"
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        aria-label="Rows per page"
                    >
                        {[25, 50, 100, 150, 200].map(sz => (
                            <option key={sz} value={sz}>{sz}</option>
                        ))}
                    </select>
                </div>
            </div>

        </div>
    );
};
